import { updateQueryString, toKebabCase } from './query';
import { CategoryNode } from './category';

export interface CustomTeamFilter {
  // businessUnit: string[];
  customTeamFilterType: 'custom';
  departments: string[];
  owners: string[];
  businessUnits: string[];
}

export interface CustomFocusFilter {
  enterpriseRequired: boolean;
  guidance: boolean;
  proposed: boolean;
}

export interface InsightsSelectedFilters {
  teams:
    | 'member'
    | 'hr-reports'
    | 'volume-testing'
    | HrReportsByLeaderTeamFilter
    | CustomTeamFilter
    | CopyURLTeamFilter;
  priority: string[];
  severity: string[];
  measurementChecksExclude: string[];
  isCustom: boolean;
}

interface HrReportsByLeaderTeamFilter {
  customTeamFilterType: 'hr-reports-by-leader';
  leader: string;
}

interface CopyURLTeamFilter {
  customTeamFilterType: 'copy-url';
  owner: string;
  hrReports: boolean;
}

interface FilterConfigSingleValue {
  title: string;
  value: string;
}

interface FilterConfigMultiValue {
  title: string;
  values: FilterConfigSingleValue[];
}

interface FilterConfigTreeMultiValue {
  title: string;
  values: CategoryNode[];
}

export interface FilterConfig {
  teams: FilterConfigSingleValue[];
  departments: FilterConfigMultiValue;
  owners: FilterConfigMultiValue;
  businessUnits: FilterConfigMultiValue;
  priority?: FilterConfigMultiValue;
  severity?: FilterConfigMultiValue;
  measurementChecks: FilterConfigTreeMultiValue;
}

// Accepts an optional environment parameter for future customization
export const defaultFilterConfig = (environment?: string): FilterConfig => ({
  teams: [
    { title: "Teams I'm a member of", value: 'member' },
    { title: 'Custom grouping', value: 'custom' },
    ...(environment && environment !== 'prod'
      ? [{ title: 'Volume Testing', value: 'volume-testing' }]
      : []),
  ],
  departments: { title: 'Department', values: [] },
  owners: {
    title: 'Owning team(s)',
    values: [],
  },
  businessUnits: { title: 'Business Unit', values: [] },
  priority: {
    title: 'Priority',
    values: [
      { title: 'Required', value: 'ent-required' },
      { title: 'Recommended', value: 'best-practice' },
    ],
  },
  severity: {
    title: 'Severity',
    values: [
      { title: 'Immediate', value: 'immediate' },
      { title: 'Critical', value: 'critical' },
      { title: 'High', value: 'high' },
      { title: 'Medium', value: 'medium' },
      { title: 'N/A', value: 'na' },
    ],
  },
  measurementChecks: {
    title: 'Measurement & checks',
    values: [],
  },
});

export const hrLeaderFilterConfig: FilterConfig = {
  teams: [
    { title: "Teams I'm a member of", value: 'member' },
    { title: 'Teams of my HR reports', value: 'hr-reports' },
    { title: 'Custom grouping', value: 'custom' },
  ],
  departments: { title: 'Department', values: [] },
  owners: {
    title: 'Owning team(s)',
    values: [],
  },
  businessUnits: { title: 'Business Unit', values: [] },
  priority: {
    title: 'Priority',
    values: [
      { title: 'Required', value: 'ent-required' },
      { title: 'Recommended practices', value: 'best-practice' },
    ],
  },
  severity: {
    title: 'Severity',
    values: [
      { title: 'Immediate', value: 'immediate' },
      { title: 'Critical', value: 'critical' },
      { title: 'High', value: 'high' },
      { title: 'Medium', value: 'medium' },
      { title: 'N/A', value: 'na' },
    ],
  },
  measurementChecks: {
    title: 'Measurement & checks',
    values: [],
  },
};

const mapToGroup = (value?: string | (string | null)[] | null) => {
  if (!value) return [];

  if (typeof value === 'string') {
    // check string for the presence of a comma and split all of them into an array of values and then map
    return value.includes(',')
      ? value.split(',').map(owner => `group:${owner}`)
      : [`group:${value}`];
  }

  return value.map(owner => `group:${owner}`);
};

const mapToArray = (value?: string | (string | null)[] | null) => {
  if (!value) return [];

  if (typeof value === 'string') {
    // check string for the presence of a comma and split all of them into an array of values and then map
    return value.includes(',') ? value.split(',') : [value];
  }

  return value;
};

export const isCustomTeamsFilter = (
  filter: InsightsSelectedFilters['teams'],
): filter is
  | CustomTeamFilter
  | HrReportsByLeaderTeamFilter
  | CopyURLTeamFilter => {
  // TODO: More robust
  return (
    typeof filter === 'object' && filter.customTeamFilterType !== undefined
  );
};

export const isHrReportsByLeaderTeamFilter = (
  filter: InsightsSelectedFilters['teams'],
): filter is HrReportsByLeaderTeamFilter => {
  return (
    typeof filter === 'object' &&
    filter.customTeamFilterType === 'hr-reports-by-leader'
  );
};

/**
 * Gets all parent category IDs from measurement data
 * @param nodes The category nodes data
 * @returns An array of parent category IDs
 */
export const getParentCategoryIds = (nodes: CategoryNode[]): string[] => {
  return nodes.map(node => node.id);
};

/**
 * Gets all child measurement IDs from measurement data
 * @param nodes The category nodes data
 * @returns An array of child measurement IDs
 */
export const getChildMeasurementIds = (nodes: CategoryNode[]): string[] => {
  return nodes.flatMap(node =>
    node.measurements ? node.measurements.map(m => m.id) : [],
  );
};

/**
 *
 * @param excludes The full list of excluded IDs (including parent categories)
 * @param nodes The category nodes data
 * @returns A filtered list containing only child measurement IDs
 */
export const filterMeasurementChecksForUrl = (
  excludes: string[],
  nodes: CategoryNode[],
): string[] => {
  if (!nodes || nodes.length === 0 || !excludes || excludes.length === 0) {
    return excludes || [];
  }

  const parentIds = getParentCategoryIds(nodes);
  const childIds = getChildMeasurementIds(nodes);

  // Create a map of original IDs to their URL-friendly versions
  const idMap: Record<string, string> = {};

  nodes.forEach(category => {
    if (category.measurements) {
      category.measurements.forEach(measurement => {
        // Create URL-friendly version of the ID
        const urlFriendlyId = measurement.id.toLowerCase().replace(/ /g, '-');

        // Store the mapping
        idMap[measurement.id] = urlFriendlyId;
      });
    }
  });

  return excludes
    .filter(id => childIds.includes(id) && !parentIds.includes(id))
    .map(id => {
      return idMap[id] || id.toLowerCase().replace(/ /g, '-');
    });
};

/**
 * Gets a mapping of child measurement IDs to their parent category ID
 * @param nodes The category nodes data
 * @returns A mapping of child to parent
 */
export const getChildToParentMapping = (
  nodes: CategoryNode[],
): Record<string, string> => {
  const mapping: Record<string, string> = {};
  nodes.forEach(node => {
    if (node.measurements) {
      node.measurements.forEach(m => {
        mapping[m.id] = node.id;
      });
    }
  });
  return mapping;
};

/**
 * Updates the excluded IDs list to maintain parent-child consistency when loading from URL
 * If a child measurement is excluded, its parent category should also be excluded (unchecked)
 *
 * @param excludedIds List of IDs to be excluded
 * @param nodes The category nodes data
 * @returns Updated list with consistent parent-child relationships
 */
export const updateExcludesForConsistency = (
  excludedIds: string[],
  nodes: CategoryNode[],
): string[] => {
  if (
    !nodes ||
    nodes.length === 0 ||
    !excludedIds ||
    excludedIds.length === 0
  ) {
    return excludedIds;
  }

  const updatedExcludes = [...excludedIds];
  const childToParentMap = getChildToParentMapping(nodes);

  // For each excluded child, make sure its parent is also excluded
  excludedIds.forEach(id => {
    const parentId = childToParentMap[id];
    if (parentId && !updatedExcludes.includes(parentId)) {
      updatedExcludes.push(parentId);
    }
  });

  return updatedExcludes;
};

// Helper function to check if a parameter exists in URL in either camelCase or kebab-case
const getParamValue = (
  search: URLSearchParams,
  paramName: string,
): string | null => {
  // Special handling for measurement-checks-exclude/measurementChecksExclude
  if (
    paramName === 'measurementChecksExclude' ||
    paramName === 'measurement-checks-exclude'
  ) {
    // Always prioritize the kebab-case version
    if (search.has('measurement-checks-exclude')) {
      return search.get('measurement-checks-exclude');
    }
    if (search.has('measurementChecksExclude')) {
      return search.get('measurementChecksExclude');
    }
  }

  // Try kebab-case version (preferred)
  const kebabCaseParam = toKebabCase(paramName);
  if (search.has(kebabCaseParam)) {
    return search.get(kebabCaseParam);
  }

  // For backward compatibility, try the original parameter name
  if (search.has(paramName)) {
    return search.get(paramName);
  }

  return null;
};

// Helper function to check if a parameter exists in URL in either camelCase or kebab-case
const hasParam = (search: URLSearchParams, paramName: string): boolean => {
  // Special handling for measurement-checks-exclude/measurementChecksExclude
  if (
    paramName === 'measurementChecksExclude' ||
    paramName === 'measurement-checks-exclude'
  ) {
    return (
      search.has('measurement-checks-exclude') ||
      search.has('measurementChecksExclude')
    );
  }

  const kebabCaseParam = toKebabCase(paramName);
  return search.has(kebabCaseParam) || search.has(paramName);
};

/**
 * Default filter values - single source of truth for filter defaults
 * Used by both URL parsing (readFilters) and reset functionality
 */
export const DEFAULT_INSIGHTS_SELECTED_FILTERS: InsightsSelectedFilters = {
  teams: 'member',
  priority: ['ent-required'],
  severity: ['immediate', 'critical', 'high', 'medium', 'na'],
  measurementChecksExclude: [],
  isCustom: false,
};

export const readFilters = (
  search: URLSearchParams,
  measurementNodes: CategoryNode[] = [],
): InsightsSelectedFilters => {
  let teams: InsightsSelectedFilters['teams'];
  // return teams for the hr-reports of the leader and set the focus to enterprise-required
  if (hasParam(search, 'hr-reports-by-leader')) {
    teams = {
      customTeamFilterType: 'hr-reports-by-leader',
      leader: getParamValue(search, 'hr-reports-by-leader') as string,
    };
  } else if (hasParam(search, 'owner')) {
    teams = {
      customTeamFilterType: 'copy-url',
      owner: getParamValue(search, 'owner') as string,
      hrReports: getParamValue(search, 'hr-reports') === 'true' ? true : false,
    };
  } else {
    teams =
      hasParam(search, 'departments') ||
      hasParam(search, 'owners') ||
      hasParam(search, 'businessUnits')
        ? {
            customTeamFilterType: 'custom',
            departments: mapToGroup(getParamValue(search, 'departments')),
            owners: mapToGroup(getParamValue(search, 'owners')),
            businessUnits: mapToArray(
              getParamValue(search, 'business-units'),
            ).filter((bu): bu is string => bu !== null),
          }
        : ((getParamValue(search, 'teams') ||
            DEFAULT_INSIGHTS_SELECTED_FILTERS.teams) as InsightsSelectedFilters['teams']);
  }

  const priority: InsightsSelectedFilters['priority'] = hasParam(
    search,
    'priority',
  )
    ? getParamValue(search, 'priority')?.split(',') || []
    : DEFAULT_INSIGHTS_SELECTED_FILTERS.priority;

  const severity: InsightsSelectedFilters['severity'] = hasParam(
    search,
    'severity',
  )
    ? getParamValue(search, 'severity')?.split(',') || []
    : DEFAULT_INSIGHTS_SELECTED_FILTERS.severity;

  // Extract measurement checks from URL - check both camelCase and kebab-case
  const measurementChecksExcludeValue =
    getParamValue(search, 'measurementChecksExclude') ||
    getParamValue(search, 'measurement-checks-exclude');

  // Process the measurement checks - normalize casing and handle hyphens
  let measurementChecks: string[] = [];
  if (measurementChecksExcludeValue) {
    // Parse the comma-separated list of excluded checks
    let parsedChecks = measurementChecksExcludeValue.split(',');

    // If we have measurement nodes, map hyphenated check IDs back to their original IDs
    if (measurementNodes.length > 0) {
      // Get all measurement IDs from nodes for matching
      const allMeasurementIds: string[] = [];
      measurementNodes.forEach(category => {
        if (category.measurements) {
          category.measurements.forEach(measurement => {
            allMeasurementIds.push(measurement.id);
          });
        }
      });

      // For each parsed check, find the matching original ID
      parsedChecks = parsedChecks.map(check => {
        const variations = [
          check,
          check.replace(/-/g, ' '),
          check.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        ];

        // Find a matching original ID
        for (const variation of variations) {
          const match = allMeasurementIds.find(
            id => id.toLowerCase() === variation.toLowerCase(),
          );
          if (match) {
            return match; // Return the original ID with correct casing
          }
          const wordMatchCheck = allMeasurementIds.find(
            id =>
              id.replace(/[^a-z0-9]/gi, '').toLowerCase() ===
              variation.replace(/[^a-z0-9]/gi, '').toLowerCase(),
          );
          if (wordMatchCheck) {
            return wordMatchCheck;
          }
        }
        return check; // Fallback to original if no match
      });
    }

    measurementChecks = parsedChecks;
  }

  // If we have measurement data and excluded checks, update the list for parent-child consistency
  if (measurementNodes.length > 0 && measurementChecks.length > 0) {
    measurementChecks = updateExcludesForConsistency(
      measurementChecks,
      measurementNodes,
    );
  }

  const isCustom = measurementChecks.length !== 0;

  return {
    teams,
    priority,
    severity,
    measurementChecksExclude: measurementChecks,
    isCustom,
  };
};

export const saveFilters = (
  filters: InsightsSelectedFilters,
  userId: string | null,
  isHrLeader: boolean | null,
  nodes: CategoryNode[] = [],
) => {
  if (
    isCustomTeamsFilter(filters.teams) &&
    filters.teams.customTeamFilterType === 'copy-url' &&
    filters.teams.owner === userId
  ) {
    updateQueryString({
      teams: filters.teams.hrReports && isHrLeader ? 'hr-reports' : 'member',
      priority: filters.priority.length > 0 ? filters.priority : undefined,
      severity: filters.severity.length > 0 ? filters.severity : undefined,
      'measurement-checks-exclude':
        filters.measurementChecksExclude &&
        filters.measurementChecksExclude.length > 0
          ? filterMeasurementChecksForUrl(
              filters.measurementChecksExclude,
              nodes,
            )
          : undefined,
    });
  } else {
    let teams: string | undefined;

    if (isCustomTeamsFilter(filters.teams)) {
      teams = undefined;
    } else if (filters.teams === 'hr-reports' && isHrLeader === false) {
      // Only change hr-reports to member if isHrLeader is explicitly false
      // This prevents changing during loading when isHrLeader might be null/undefined
      teams = 'member';
    } else {
      teams = filters.teams;
    }

    // Create query parameters object
    const queryParams: Record<string, any> = {
      teams,
      priority: filters.priority.length > 0 ? filters.priority : undefined,
      severity: filters.severity.length > 0 ? filters.severity : undefined,
      'measurement-checks-exclude':
        filters.measurementChecksExclude &&
        filters.measurementChecksExclude.length > 0
          ? filterMeasurementChecksForUrl(
              filters.measurementChecksExclude,
              nodes,
            )
          : undefined,
    };

    // Add custom team filters if necessary
    if (
      isCustomTeamsFilter(filters.teams) &&
      filters.teams.customTeamFilterType === 'custom'
    ) {
      queryParams.departments = filters.teams.departments.map(
        (department: string) => department.replace('group:', ''),
      );
      queryParams.owners = filters.teams.owners.map((owner: string) =>
        owner.replace('group:', ''),
      );
      queryParams.businessUnits = filters.teams.businessUnits;
    }

    // Add HR reports by leader
    if (isHrReportsByLeaderTeamFilter(filters.teams)) {
      queryParams['hr-reports-by-leader'] = filters.teams.leader;
    }

    // Add copy URL params
    if (
      isCustomTeamsFilter(filters.teams) &&
      filters.teams.customTeamFilterType === 'copy-url'
    ) {
      queryParams.owner = filters.teams.owner;
      if (filters.teams.hrReports) {
        queryParams['hr-reports'] = 'true';
      }
    }

    updateQueryString(queryParams);
  }

  // Dispatch a custom event to notify components the filters have been saved
  window.dispatchEvent(
    new CustomEvent('filtersSaved', {
      detail: { filters },
    }),
  );
};

export const getAllChecks = (
  nodes: CategoryNode[],
  applied: Record<string, any>,
): string[] => {
  const excludedValues = applied.measurementChecksExclude.map((value: string) =>
    value.toLowerCase(),
  );

  const checks: string[] = nodes.flatMap(node => {
    // Include each category's measurements regardless of category exclusion status
    if (node.measurements) {
      return node.measurements.flatMap(measurement => {
        // Include measurement's checks even if parent category is excluded
        // Only exclude if the specific measurement itself is excluded
        if (!excludedValues.includes(measurement.id.toLowerCase())) {
          return measurement.checks.map(check => check.name);
        }
        return [];
      });
    }
    return [];
  });

  return checks;
};
